import {
	Alert,
	Box,
	Button,
	Chip,
	Collapse,
	Divider,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import constants from '../../constants';
import SubsidyOffersTable, { OfferRow, OfferStatus, STATUS_LABELS, TIER_LABELS } from '../Company/SubsidyOffersTable';

// GH-697 (Udi, 14.9.2026): one chronological page of every smart-budget offer across ALL companies,
// with the same columns as the per-company budget tab. Read-only: GET /company-subsidy/admin/offers.

const PAGE_LIMIT = 500;

// GH-796 (Uri + Udi, 13.10.2026): demo offer — a REAL offer (email + WhatsApp + push + magic page) sent to an
// existing member by email, so Udi can show an HR manager what employees get. The backend keeps it out of every
// budget / cycle / HR number; this page sends it (POST /company-subsidy/admin/demo-offer) and lists the sent
// demos (GET /company-subsidy/admin/offers?demo=only).
const DEMO_LIST_LIMIT = 100;
const DEMO_DEFAULT_DAYS = 7;
const HEADLINES: Record<'A' | 'B', string> = {
	A: 'הוקצתה לך הטבה אישית בשווי מאות שקלים',
	B: 'קיבלת הטבה ייחודית עבורך',
};

interface DemoResult {
	offer: OfferRow;
	member: { _id: string; first_name?: string; last_name?: string; email?: string };
	company: { _id: string; name: string };
	/** What the member could be reached by (demos skip the dormant rule). */
	channels: { email: boolean; whatsapp: boolean; push: boolean };
	/** Magic page WITHOUT `&c=` — opening it does not stamp clicked_at. */
	preview_link: string;
}

const companyOf = (o: OfferRow): { id: string; name: string } | null =>
	typeof o.company === 'object' && o.company
		? { id: o.company._id, name: o.company.english_name || o.company._id }
		: typeof o.company === 'string'
			? { id: o.company, name: o.company }
			: null;

export default function AllSubsidyOffers() {
	const notify = useNotify();
	const [offers, setOffers] = useState<OfferRow[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [statusFilter, setStatusFilter] = useState<OfferStatus | 'all'>('all');
	const [companyFilter, setCompanyFilter] = useState<string>('all');
	const [yearFilter, setYearFilter] = useState<number | 'all'>('all');

	// GH-796 — demo offer form + the "sent demos" list
	const [demoEmail, setDemoEmail] = useState('');
	const [demoVariant, setDemoVariant] = useState<'A' | 'B'>('A');
	const [demoDays, setDemoDays] = useState(String(DEMO_DEFAULT_DAYS));
	const [demoSending, setDemoSending] = useState(false);
	const [demoResult, setDemoResult] = useState<DemoResult | null>(null);
	const [demos, setDemos] = useState<OfferRow[]>([]);
	const [demosTotal, setDemosTotal] = useState(0);
	const [demosLoading, setDemosLoading] = useState(true);
	const [demosOpen, setDemosOpen] = useState(false);

	const loadDemos = useCallback(async () => {
		setDemosLoading(true);
		try {
			const res = await axios.get(`${constants.API_URL}/company-subsidy/admin/offers?demo=only&limit=${DEMO_LIST_LIMIT}`);
			setDemos((res.data?.docs || []) as OfferRow[]);
			setDemosTotal(res.data?.totalDocs ?? res.data?.docs?.length ?? 0);
		} catch (e: any) {
			notify(e?.response?.data?.message || 'טעינת ההדגמות נכשלה', { type: 'error' });
		} finally {
			setDemosLoading(false);
		}
	}, [notify]);

	useEffect(() => {
		loadDemos();
	}, [loadDemos]);

	const sendDemo = async () => {
		const email = demoEmail.trim();
		if (!email || demoSending) return;
		const days = Number(demoDays);
		if (!Number.isInteger(days) || days < 1 || days > 14) {
			notify('מספר הימים חייב להיות בין 1 ל-14', { type: 'warning' });
			return;
		}
		if (
			!window.confirm(
				`לשלוח הקצאת הדגמה ל-${email}?\nכותרת ${demoVariant} · בתוקף ${days} ימים.\n\nזו הצעה אמיתית: המנוי יקבל מייל, וואטסאפ ופוש, וקביעת פגישה דרכה היא פגישה אמיתית.`,
			)
		)
			return;
		setDemoSending(true);
		setDemoResult(null);
		try {
			// A member who opted out of subsidy offers gets a 409 — never overridden from here.
			const res = await axios.post(`${constants.API_URL}/company-subsidy/admin/demo-offer`, {
				email,
				variant: demoVariant,
				days,
			});
			setDemoResult(res.data as DemoResult);
			setDemoEmail('');
			notify('הקצאת ההדגמה נשלחה', { type: 'success' });
			setDemosOpen(true);
			loadDemos();
		} catch (e: any) {
			notify(e?.response?.data?.message || 'שליחת הקצאת ההדגמה נכשלה', { type: 'error' });
		} finally {
			setDemoSending(false);
		}
	};

	const copyPreviewLink = () => {
		if (!demoResult?.preview_link) return;
		navigator.clipboard?.writeText(demoResult.preview_link);
		notify('הקישור הועתק', { type: 'info' });
	};

	useEffect(() => {
		(async () => {
			setLoading(true);
			try {
				const res = await axios.get(`${constants.API_URL}/company-subsidy/admin/offers?limit=${PAGE_LIMIT}`);
				setOffers((res.data?.docs || []) as OfferRow[]);
				setTotal(res.data?.totalDocs ?? res.data?.docs?.length ?? 0);
			} catch (e: any) {
				notify(e?.response?.data?.message || 'טעינת ההצעות נכשלה', { type: 'error' });
			} finally {
				setLoading(false);
			}
		})();
	}, [notify]);

	const companies = useMemo(() => {
		const map = new Map<string, string>();
		for (const o of offers) {
			const c = companyOf(o);
			if (c) map.set(c.id, c.name);
		}
		return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], 'he'));
	}, [offers]);

	const years = useMemo(
		() => Array.from(new Set(offers.map((o) => o.cycle_year).filter(Boolean))).sort((a, b) => b - a),
		[offers],
	);

	const filtered = useMemo(
		() =>
			offers.filter(
				(o) =>
					(statusFilter === 'all' || o.status === statusFilter) &&
					(companyFilter === 'all' || companyOf(o)?.id === companyFilter) &&
					(yearFilter === 'all' || o.cycle_year === yearFilter),
			),
		[offers, statusFilter, companyFilter, yearFilter],
	);

	// GH-796 — demo offers never enter the program counts or the A/B read-out. The backend already leaves them out
	// of this list by default; defence in depth in case it is ever loaded with ?demo=include.
	const realFiltered = useMemo(() => filtered.filter((o) => !o.is_demo), [filtered]);

	const counts = useMemo(() => {
		const c: Record<OfferStatus, number> = { offered: 0, accepted: 0, declined: 0, expired: 0, cancelled: 0 };
		for (const o of realFiltered) c[o.status] = (c[o.status] || 0) + 1;
		return c;
	}, [realFiltered]);

	// GH-727 — headline A/B read-out: sent / clicked / accepted per variant (cancelled offers excluded,
	// they never reached a decision). Offers created before the test have no variant and are not counted.
	const ab = useMemo(() => {
		const init = () => ({ sent: 0, clicked: 0, accepted: 0 });
		const r: Record<string, ReturnType<typeof init>> = { A: init(), B: init() };
		for (const o of realFiltered) {
			if (o.is_demo || !o.copy_variant || o.status === 'cancelled') continue; // GH-796: never demos
			const v = (r[o.copy_variant] ||= init());
			v.sent += 1;
			if (o.clicked_at) v.clicked += 1;
			if (o.status === 'accepted') v.accepted += 1;
		}
		return r;
	}, [realFiltered]);
	const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : '—');

	return (
		<Box p={2} dir="rtl">
			<Typography variant="h5">מימושי תקציב חכם — כל החברות</Typography>
			<Typography variant="body2" color="text.secondary" mb={2}>
				כל ההצעות מכל החברות לפי סדר כרונולוגי (החדשה למעלה), עם אותם שדות כמו בטאב ניהול התקציב בתוך החברה. מזהה אישי — לעיני אדמין בלבד.
			</Typography>

			<Grid container spacing={2}>
				{/* GH-796 — send a demo offer + the list of demos already sent */}
				<Grid item xs={12}>
					<Paper sx={{ p: 2 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
							הקצאת הדגמה
						</Typography>
						<Typography variant="body2" color="text.secondary" mb={1.5}>
							שליחת הצעה אמיתית (מייל, וואטסאפ, פוש ועמוד הזמנה) למנוי קיים לפי המייל שלו, כדי להראות ל-HR מה העובדים מקבלים.
							{` ${TIER_LABELS.intro}. `}
							ההדגמה לא נספרת בתקציב, במחזור, בסיכומים או במבחן ה-A/B, ולא מופיעה ברשימות ההקצאות של HR. אם המנוי קובע דרכה פגישה — זו פגישה אמיתית (המומחה מקבל תשלום והיא נספרת בסך הפגישות של החברה).
						</Typography>
						<Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
							<TextField
								size="small"
								type="email"
								label="מייל המנוי"
								value={demoEmail}
								onChange={(e) => setDemoEmail(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') sendDemo();
								}}
								inputProps={{ dir: 'ltr' }}
								sx={{ minWidth: 260 }}
							/>
							<FormControl size="small" sx={{ minWidth: 320 }}>
								<InputLabel>כותרת</InputLabel>
								<Select value={demoVariant} label="כותרת" onChange={(e) => setDemoVariant(e.target.value as 'A' | 'B')}>
									{(['A', 'B'] as const).map((v) => (
										<MenuItem key={v} value={v}>
											{`${v} · ״${HEADLINES[v]}״`}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<TextField
								size="small"
								type="number"
								label="ימים בתוקף"
								value={demoDays}
								onChange={(e) => setDemoDays(e.target.value)}
								inputProps={{ min: 1, max: 14 }}
								sx={{ width: 120 }}
							/>
							<Button variant="contained" onClick={sendDemo} disabled={demoSending || !demoEmail.trim()}>
								{demoSending ? 'שולח…' : 'שלח הקצאת הדגמה'}
							</Button>
						</Stack>

						{demoResult && (
							<Alert severity="success" sx={{ mt: 2 }} onClose={() => setDemoResult(null)}>
								<Typography variant="body2" sx={{ fontWeight: 700 }}>
									{`נשלחה הקצאת הדגמה ל${
										`${demoResult.member.first_name ?? ''} ${demoResult.member.last_name ?? ''}`.trim() || 'מנוי'
									}${demoResult.member.email ? ` (${demoResult.member.email})` : ''} · ${demoResult.company.name || '—'}`}
								</Typography>
								<Typography variant="body2">
									{`כותרת ${demoResult.offer.copy_variant ?? demoVariant} · ${TIER_LABELS[demoResult.offer.intervention_category] ?? ''} · ₪${
										demoResult.offer.price
									} · בתוקף עד ${new Date(demoResult.offer.offer_expires_at).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' })}`}
								</Typography>
								<Typography variant="body2">
									{`ערוצים זמינים למנוי: מייל ${demoResult.channels.email ? '✓' : '✗'} · וואטסאפ ${
										demoResult.channels.whatsapp ? '✓' : '✗'
									} · פוש ${demoResult.channels.push ? '✓' : '✗'}`}
								</Typography>
								<Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}>
									<TextField
										size="small"
										value={demoResult.preview_link}
										InputProps={{ readOnly: true }}
										inputProps={{ dir: 'ltr' }}
										onFocus={(e) => e.target.select()}
										sx={{ flex: 1, minWidth: 280 }}
									/>
									<Button size="small" variant="outlined" onClick={copyPreviewLink}>
										העתקת קישור
									</Button>
									<Button size="small" href={demoResult.preview_link} target="_blank" rel="noopener noreferrer">
										פתיחה
									</Button>
								</Stack>
								<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
									קישור תצוגה לעמוד ההצעה — פתיחה שלו לא נרשמת כלחיצה של המנוי. קביעת פגישה דרכו היא קביעה אמיתית. הקישור אישי: מוצגים בו שם המנוי וההתאמות מהשאלון שלו — לא להעביר אותו הלאה.
								</Typography>
							</Alert>
						)}

						<Divider sx={{ my: 2 }} />
						<Stack direction="row" alignItems="center" sx={{ gap: 1 }}>
							<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
								{`הדגמות שנשלחו (${demosTotal})`}
							</Typography>
							<Button size="small" onClick={() => setDemosOpen((v) => !v)}>
								{demosOpen ? 'הסתר' : 'הצג'}
							</Button>
						</Stack>
						<Collapse in={demosOpen} timeout="auto" unmountOnExit>
							<Box sx={{ mt: 1 }}>
								{demosLoading ? (
									<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
										טוען…
									</Typography>
								) : (
									<>
										<SubsidyOffersTable offers={demos} showCompany layout="compact" emptyText="עוד לא נשלחו הדגמות." />
										{demosTotal > DEMO_LIST_LIMIT && (
											<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
												מוצגות {DEMO_LIST_LIMIT} ההדגמות האחרונות מתוך {demosTotal}
											</Typography>
										)}
									</>
								)}
							</Box>
						</Collapse>
					</Paper>
				</Grid>

				<Grid item xs={12}>
					<Paper sx={{ p: 2 }}>
						<Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
							<FormControl size="small" sx={{ minWidth: 200 }}>
								<InputLabel>חברה</InputLabel>
								<Select value={companyFilter} label="חברה" onChange={(e) => setCompanyFilter(e.target.value)}>
									<MenuItem value="all">כל החברות ({companies.length})</MenuItem>
									{companies.map(([id, name]) => (
										<MenuItem key={id} value={id}>
											{name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small" sx={{ minWidth: 150 }}>
								<InputLabel>סטטוס</InputLabel>
								<Select
									value={statusFilter}
									label="סטטוס"
									onChange={(e) => setStatusFilter(e.target.value as OfferStatus | 'all')}
								>
									<MenuItem value="all">הכל</MenuItem>
									{(['offered', 'accepted', 'declined', 'expired'] as OfferStatus[]).map((s) => (
										<MenuItem key={s} value={s}>
											{STATUS_LABELS[s]}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small" sx={{ minWidth: 120 }}>
								<InputLabel>שנת מחזור</InputLabel>
								<Select
									value={yearFilter}
									label="שנת מחזור"
									onChange={(e) => setYearFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
								>
									<MenuItem value="all">הכל</MenuItem>
									{years.map((y) => (
										<MenuItem key={y} value={y}>
											{y}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<Box flexGrow={1} />
							<Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
								<Chip size="small" label={`סה"כ ${realFiltered.length}`} />
								<Chip size="small" color="warning" variant="outlined" label={`בהמתנה ${counts.offered}`} />
								<Chip size="small" color="success" variant="outlined" label={`התקבלו ${counts.accepted}`} />
								<Chip size="small" color="error" variant="outlined" label={`נדחו ${counts.declined}`} />
								<Chip size="small" variant="outlined" label={`פקעו ${counts.expired}`} />
							</Stack>
						</Stack>
						{(ab.A.sent > 0 || ab.B.sent > 0) && (
							<Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
								<Typography variant="body2" sx={{ fontWeight: 700 }}>
									מבחן כותרת A/B:
								</Typography>
								{(['A', 'B'] as const).map((v) => (
									<Chip
										key={v}
										size="small"
										sx={{ bgcolor: v === 'B' ? '#FEF3C7' : '#E0F2FE', color: '#00224B' }}
										label={`${v} · נשלחו ${ab[v].sent} · לחצו ${ab[v].clicked} (${pct(ab[v].clicked, ab[v].sent)}) · מומשו ${ab[v].accepted} (${pct(ab[v].accepted, ab[v].sent)})`}
									/>
								))}
								<Typography variant="caption" color="text.secondary">
									A = ״הוקצתה לך הטבה אישית בשווי מאות שקלים״ · B = ״קיבלת הטבה ייחודית עבורך״ · הסינון למעלה חל גם כאן
								</Typography>
							</Stack>
						)}
					</Paper>
				</Grid>

				<Grid item xs={12}>
					<Paper sx={{ p: 2 }}>
						{loading ? (
							<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
								טוען…
							</Typography>
						) : (
							<>
								<SubsidyOffersTable offers={filtered} showCompany layout="compact" emptyText="אין הצעות שמתאימות לסינון." />
								{total > PAGE_LIMIT && (
									<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
										מוצגות {PAGE_LIMIT} ההצעות האחרונות מתוך {total}
									</Typography>
								)}
							</>
						)}
					</Paper>
				</Grid>
			</Grid>
		</Box>
	);
}
